import { zodResolver } from '@hookform/resolvers/zod'
import { Box, ButtonPropsColorOverrides, CircularProgress, Paper, useMediaQuery, useTheme } from '@mui/material'
import { OverridableStringUnion } from '@mui/types'
import React, { useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '../../../app/hooks'
import { useLoggedInUser } from '../../../app/hooks/useLoggedInUser'
import { useNotificationStack } from '../../../app/hooks/useNotificationStack'
import { useTrackingNumberTemplateQuery } from '../../../app/redux-fetch/apiQuery'
import {
  apiOrders,
  useAbandonOrderMutation,
  useCreateOrderMutation,
  useCreateShipmentMutation,
  useSubmitOrderMutation,
  useUpdateShipmentMutation,
} from '../../../app/redux-fetch/apiOrders'
import BoxAlert from '../../../components/Alerts/BoxAlert'
import CustomAlert from '../../../components/Alerts/CustomAlert'
import AppStepper from '../../../components/AppStepper/AppStepper'
import GenericButton from '../../../components/Buttons/GenericButton'
import TooltipButton from '../../../components/Buttons/TooltipButton'
import { useConfirmDialogContext } from '../../../components/ConfirmDialog'
import commonMessages from '../../../components/Messages/commonMessages'
import { MutationSpinnerBoxCenter } from '../../../components/Spinner/styles'
import { OrderStatuses } from '../../../types/common'
import { OrderFormProvider } from '../context/OrderFormContext'
import ConsignmentList from './ConsignmentList'
import messages from './messages'
import { IShipmentFormInput, schema } from './schema'
import ShipmentInformation from './ShipmentInformation'
import ShipmentSummary from './ShipmentSummary'
import { mapToShipmentData } from './utils'
import { validateShipmentData, ValidationError } from './utils/crossValidation'

interface ShipmentFormProps {
  initialValues?: Partial<IShipmentFormInput>
  onSubmit: (data: IShipmentFormInput) => void
  isEdit?: boolean
  onCancel?: () => void
}

const ShipmentForm: React.FC<ShipmentFormProps> = ({ initialValues, isEdit = false, onSubmit }) => {
  const { tenantId, customerId, isTenantUser } = useLoggedInUser()

  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()

  const methods = useForm<IShipmentFormInput>({
    resolver: zodResolver(schema),
    mode: 'all',
    criteriaMode: 'all',
    defaultValues: initialValues || {
      tenantId: tenantId,
      customerId: customerId,
      bookings: [
        {
          shipFrom: {},
          deliverTo: {},
        },
      ],
    },
  })

  const {
    handleSubmit,
    reset,
    formState: { errors, isDirty, dirtyFields },

    setValue,
    watch,
    trigger,
  } = methods


  useEffect(() => {
    if (initialValues) {
      reset(initialValues)
    } else {
      const defaultState = {
        tenantId: tenantId,
        customerId: customerId,
        bookings: [
          {
            shipFrom: {},
            deliverTo: {},
          },
        ],
      }
    }
  }, [initialValues, reset, tenantId, customerId])

  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const getPageFromHash = () => {
    if (isEdit) {
      const hash = window.location.hash
      if (hash) {
        const pageNumber = parseInt(hash.substring(1), 10)
        if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= 4) {
          return pageNumber
        }
      }
    }
    return 1
  }

  const [currentPage, setCurrentPage] = useState(getPageFromHash())

  useEffect(() => {
    if (isEdit) {
      window.history.replaceState(null, '', `#${currentPage}`)
    }
  }, [currentPage, isEdit])

  const [createShipment, { isLoading }] = useCreateShipmentMutation()
  const [updateShipment, { isLoading: isUpdating }] = useUpdateShipmentMutation()
  const [createOrder, { isLoading: isCreatingOrder }] = useCreateOrderMutation()
  const [abandonOrder, { isLoading: isAbandoning }] = useAbandonOrderMutation()

  const [submitOrder] = useSubmitOrderMutation()

  const { enqueueSuccess, enqueueFailure } = useNotificationStack()
  const { confirm } = useConfirmDialogContext()

  const [isLoadRequired, setIsLoadRequired] = useState(false)
  const [isLoadContentRequired, setIsLoadContentRequired] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([])

  const isTrulyDirty = useCallback(() => {
    if (!isDirty) return false

    const dirtyFieldsKeys = Object.keys(dirtyFields)

    if (dirtyFieldsKeys.length === 0 && isDirty) {
      return false
    }

    const currentValues = methods.getValues()
    const bookings = currentValues.bookings || []

    const automaticFields: string[] = []

    bookings.forEach((booking: any, bookingIndex: number) => {
      const packaging = booking.packaging || []
      packaging.forEach((packagingItem: any, packagingIndex: number) => {
        automaticFields.push(
          `bookings.${bookingIndex}.packaging.${packagingIndex}.isClearingRequired`,
          `bookings.${bookingIndex}.packaging.${packagingIndex}.isCrossBorder`,
          `bookings.${bookingIndex}.packaging.${packagingIndex}.physicalProperties.tareWeight`
        )
      })
    })

    const hasNonAutomaticChanges = dirtyFieldsKeys.some(
      (key) => !automaticFields.some((automaticField) => key.startsWith(automaticField))
    )

    return hasNonAutomaticChanges
  }, [isDirty, dirtyFields, methods])

  useEffect(() => {
    const subscription = watch((value) => {
      const validateData = async () => {
        if (value.tenantId && value.customerId && value.bookings) {
          const errors = validateShipmentData(value as IShipmentFormInput)
          setValidationErrors(errors)
        } else {
          setValidationErrors([])
        }
      }
      validateData()
    })
    return () => subscription.unsubscribe()
  }, [watch])

  const getSuccessMessage = useCallback(
    (isEdit: boolean, currentStatus?: string | null, currentPage?: number, changeStatus = true) => {
      if (!isEdit) {
        return messages.shipmentUpdated
      }

      if (currentStatus === OrderStatuses.Draft && currentPage === 3 && changeStatus) {
        return messages.shipmentCreated
      }

      if (currentStatus === OrderStatuses.Draft) {
        return messages.shipmentUpdated
      }

      if (currentStatus === OrderStatuses.Created) {
        return messages.shipmentSaved
      }

      return messages.shipmentUpdated
    },
    []
  )

  const getFailureMessage = useCallback(
    (isEdit: boolean, currentStatus?: string | null, currentPage?: number) => {
      if (!isEdit) {
        return messages.shipmentCreateFailed
      }

      if (currentStatus === OrderStatuses.Draft && currentPage === 3) {
        return messages.shipmentCreateFailed
      }

      if (currentStatus === OrderStatuses.Draft) {
        return messages.shipmentSaveDraftFailed
      }

      if (currentStatus === OrderStatuses.Created) {
        return messages.shipmentSaveFailed
      }

      return messages.shipmentSaveDraftFailed
    },
    []
  )

  const { data: template } = useTrackingNumberTemplateQuery({ tenantId, customerId })

  const isTrackingNumberSetup = useCallback(() => !!template, [template])

  const navigate = useNavigate()

  const customer = watch('customerId')
  const bookings = watch('bookings')
  const status = watch('status')

  const validateWaypoints = async () => {
    setIsValidating(true)
    try {
      const fieldsToTrigger = []

      for (let i = 0; i < bookings?.length; i++) {
        fieldsToTrigger.push(`bookings.${i}.waypoints`)
        fieldsToTrigger.push(`bookings.${i}.shipFrom`)
        fieldsToTrigger.push(`bookings.${i}.deliverTo`)
        fieldsToTrigger.push(`bookings.${i}.returnTo`)
        fieldsToTrigger.push(`bookings.${i}.containerCollections`)
        fieldsToTrigger.push(`bookings.${i}.referenceNumber`)
        fieldsToTrigger.push(`bookings.${i}.clearingRequired`)
        fieldsToTrigger.push('customerReferenceNumber')
        fieldsToTrigger.push('description')
        fieldsToTrigger.push('customerId')
      }

      return await trigger(fieldsToTrigger as any)
    } finally {
      setIsValidating(false)
    }
  }

  const validateLoads = async () => {
    setIsValidating(true)
    try {
      const fieldsToTrigger = []

      for (let i = 0; i < bookings?.length; i++) {
        const booking = bookings[i]
        if (!booking || !booking.packaging) continue

        for (let j = 0; j < booking?.packaging?.length; j++) {
          const packaging = booking.packaging[j]
          if (!packaging) continue

          const packagingFields = Object.keys(packaging)
            .filter(
              (key) =>
                key !== 'products' &&
                key !== 'declaredContentDescription' &&
                key !== 'declaredValue'
            )
            .map((key) => `bookings.${i}.packaging.${j}.${key}`)

          fieldsToTrigger.push(...packagingFields)
        }
      }

      return await trigger(fieldsToTrigger as any)
    } finally {
      setIsValidating(false)
    }
  }

  const validateLoadsWithProducts = async () => {
    setIsValidating(true)
    try {
      const fieldsToTrigger = []

      for (let i = 0; i < bookings?.length; i++) {
        const booking = bookings[i]
        if (!booking || !booking.packaging) continue

        for (let j = 0; j < booking?.packaging?.length; j++) {
          const packaging = booking.packaging[j]
          if (!packaging) continue

          const packagingFields = Object.keys(packaging).map(
            (key) => `bookings.${i}.packaging.${j}.${key}`
          )

          fieldsToTrigger.push(...packagingFields)

          if (packaging.products && Array.isArray(packaging.products)) {
            for (let k = 0; k < packaging.products.length; k++) {
              const productFields = Object.keys(packaging.products[k]).map(
                (key) => `bookings.${i}.packaging.${j}.products.${k}.${key}`
              )
              fieldsToTrigger.push(...productFields)
            }
          }
        }
      }

      return await trigger(fieldsToTrigger as any)
    } finally {
      setIsValidating(false)
    }
  }

  const handleSubmitForm = async (
    data: IShipmentFormInput,
    onAfterRequest?: (responseData?: any) => void,
    changeStatus = true
  ) => {
    const payload = mapToShipmentData(data)
    let isErrorOccurred = false
    if (payload) {
      let response
      try {
        if (isEdit) {
          response = await updateShipment({ shipment: payload, tenantId, customerId }).unwrap()
          if (currentPage === 3 && status === OrderStatuses.Draft && changeStatus) {
            await new Promise((resolve) => setTimeout(resolve, 100))
            await createOrder({ tenantId, customerId, id: payload.id ?? 0 }).unwrap()
            setValue('status', OrderStatuses.Created)

            await dispatch(apiOrders.util.invalidateTags(['Shipments', 'Orders']))

            await new Promise((resolve) => setTimeout(resolve, 500))
            if (payload.id) {
              await dispatch(
                apiOrders.endpoints.getShipmentById.initiate(
                  {
                    tenantId,
                    customerId,
                    shipmentId: payload.id,
                  },
                  { forceRefetch: true }
                )
              )
            }
            await new Promise((resolve) => setTimeout(resolve, 1000))
          }
          enqueueSuccess(getSuccessMessage(isEdit, status, currentPage, changeStatus))
        } else {
          response = await createShipment({ shipment: payload, tenantId, customerId }).unwrap()
          dispatch(apiOrders.util.invalidateTags(['Shipments']))
          enqueueSuccess(getSuccessMessage(isEdit, status, currentPage, changeStatus))
        }
      } catch (error) {
        enqueueFailure(getFailureMessage(isEdit, status, currentPage))
        isErrorOccurred = true
      } finally {
        if (onAfterRequest && !isErrorOccurred) {
          onAfterRequest(response)
        }
      }
    }
  }

  const handleSubmitShipment = async () => {
    const data = methods.getValues()
    const validationErrors = validateShipmentData(data)

    if (validationErrors.length > 0) {
      enqueueFailure(messages.validationErrorsPresent)
      return
    }

    const confirmed = await confirm({
      title: 'Submit Shipment',
      message: 'Are you sure you want to submit this shipment?',
      confirmText: 'Submit',
      cancelText: 'Cancel',
      severity: 'info',
      confirmButtonColor: 'success',
      cancelButtonColor: 'secondary',
    })

    if (confirmed) {
      handleSubmitForm(data, async (response) => {
        if (response) {
          try {
            await submitOrder({ tenantId, id: response.id }).unwrap()
            enqueueSuccess(messages.shipmentSubmitted)
            navigate('/shipping/shipments')
          } catch (error) {
            enqueueFailure(messages.shipmentSubmitFailed)
          }
        }
      })
    }
  }

  const handleSaveDraft = async () => {
    const data = methods.getValues()
    let isValid = false

    if (currentPage === 1) {
      isValid = await validateWaypoints()
    } else if (currentPage === 2) {
      isValid = await validateLoads()
    } else if (currentPage === 3) {
      isValid = await validateLoadsWithProducts()
    } else {
      isValid = true
    }
    if (!isValid) {
      return
    }

    handleSubmitForm(
      data,
      () => {
        navigate('/shipping/shipments')
      },
      false
    )
  }

  const handleAbandon = async () => {
    const confirmed = await confirm({
      title: 'Abandon Shipment',
      message: 'Are you sure you want to abandon this shipment?',
      confirmText: 'Abandon',
      cancelText: 'Cancel',
      severity: 'warning',
      confirmButtonColor: 'error',
      cancelButtonColor: 'secondary',
    })

    if (confirmed) {
      const data = methods.getValues()
      await abandonOrder({ tenantId, id: data.id ?? 0 }).unwrap()
      enqueueSuccess(messages.shipmentAbandoned)
      navigate('/shipping/shipments')
    }
  }

  const handleSaveDraftForNext = async () => {
    setIsValidating(true)
    try {
      const data = methods.getValues()
      await methods.trigger()

      data.revision = data.revision ?? 0
      try {
        let response

        await handleSubmitForm(data, (res) => {
          response = res
        })

        return response
      } catch (error) {
        enqueueFailure(getFailureMessage(isEdit, status, currentPage))
        return null
      }
    } finally {
      setIsValidating(false)
    }
  }

  const handleNext = async () => {
    let isValid = false

    if (currentPage === 1) {
      isValid = await validateWaypoints()
    } else if (currentPage === 2) {
      isValid = await validateLoads()
    } else if (currentPage === 3) {
      isValid = await validateLoadsWithProducts()
    } else {
      isValid = true
    }

    if (isValid) {
      if (!isEdit) {
        if (isDirty) {
          const draftResponse = await handleSaveDraftForNext()

          if (draftResponse) {
            const id = (draftResponse as any).id
            navigate(`/shipping/shipments/${id}/edit#${currentPage + 1}`)
            setCurrentPage((prev) => prev + 1)
          }
        } else {
          setCurrentPage((prev) => prev + 1)
        }
      } else {
        if (isDirty) {
          const draftResponse = await handleSaveDraftForNext()
          if (draftResponse) {
            setCurrentPage((prev) => prev + 1)
          }
        } else {
          if (currentPage === 3 && status === OrderStatuses.Draft) {
            const data = methods.getValues()
            try {
              await createOrder({ tenantId, customerId, id: data.id ?? 0 }).unwrap()
              setValue('status', OrderStatuses.Created)
            } catch (error) {
              enqueueFailure(getFailureMessage(isEdit, status, currentPage))
              return
            }
          }
          setCurrentPage((prev) => prev + 1)
        }
      }
    }
  }

  const handleBack = async () => {
    if (currentPage === 1) {
      return
    }

    if (isTrulyDirty()) {
      const action = await confirm({
        title: 'Unsaved Changes',
        message:
          'You have unsaved changes, and these will be lost if you move to the previous page. Do you want to Discard or Save these changes?',
        confirmText: 'Save',
        cancelText: 'Discard',
        severity: 'warning',
        confirmButtonColor: 'primary',
        cancelButtonColor: 'secondary',
      })

      if (action) {
        let isValid = false
        if (currentPage === 2) {
          isValid = await validateLoads()
        } else if (currentPage === 3) {
          isValid = await validateLoadsWithProducts()
        } else {
          isValid = true
        }

        if (isValid) {
          const data = methods.getValues()
          await handleSubmitForm(
            data,
            () => {
              setCurrentPage((prev) => prev - 1)
            },
            false
          )
        }
      } else {
        reset()
        setCurrentPage((prev) => prev - 1)
      }
    } else {
      setCurrentPage((prev) => prev - 1)
    }
  }

  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (name?.includes('shipFrom.loadingDate') || name?.includes('deliverTo.deliveryDate')) {
        methods.trigger()
      }
    })
    return () => subscription.unsubscribe()
  }, [methods])

  const steps = [
    { label: formatMessage(messages.consignments) },
    { label: formatMessage(messages.load) },
    { label: formatMessage(messages.contents) },
    { label: formatMessage(messages.consignmentServices) },
    { label: formatMessage(messages.summary) },
  ]

  const activeStep = currentPage <= 4 ? currentPage - 1 : 4

  const isConsignmentRequired =
    (!bookings || bookings?.length === 0) && (currentPage !== 1 || isEdit)

  const getTooltipMessage = () => {
    if (isConsignmentRequired) {
      return formatMessage(messages.noConsignment)
    }
    if (isLoadContentRequired && !isLoadRequired) {
      return formatMessage(messages.noLoadContent)
    }
    return formatMessage(messages.noLoad)
  }

  const isRejected = status === OrderStatuses.Rejected

  const startPoint: string | undefined = undefined

  const nextButtonText = useCallback(() => {
    if (status && status !== OrderStatuses.Draft && [1, 2].includes(currentPage)) {
      return formatMessage(commonMessages.saveAndContinue)
    }
    if (currentPage === 3 && status === OrderStatuses.Draft) {
      return formatMessage(commonMessages.saveAndCreate)
    }
    return formatMessage(commonMessages.saveAndContinue)
  }, [currentPage, status, formatMessage])

  const nextButtonColor = useCallback((): OverridableStringUnion<
    'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning',
    ButtonPropsColorOverrides
  > => {
    if (currentPage === 3 && status === OrderStatuses.Draft) {
      return 'success'
    }
    return 'primary'
  }, [currentPage, status, formatMessage])

  return (
    <Paper elevation={3} sx={{ padding: 0, boxShadow: 'none' }}>
      {isValidating && (
        <>
          <MutationSpinnerBoxCenter>
            <CircularProgress color='primary' />
          </MutationSpinnerBoxCenter>
        </>
      )}
      <Box
        sx={{
          backgroundColor: '#f0f0f0',
          textAlign: 'center',
        }}
      >
        <AppStepper activeStep={activeStep} steps={steps} />
      </Box>
      <OrderFormProvider
        currentPage={currentPage}
        totalPages={4}
        setCurrentPage={setCurrentPage}
        isShipperEnabled={false}
        startPoint={startPoint}
      >
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box
              display='flex'
              flexDirection={isSmallScreen ? 'column' : 'row'}
              alignItems='center'
              sx={{
                marginBottom: '8px',
                gap: isSmallScreen ? 2 : 3,
                width: '100%',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', gap: 0, flexWrap: 'wrap', alignItems: 'center' }}>
                <GenericButton
                  variant={'outlined'}
                  sx={{ whiteSpace: 'nowrap' }}
                  onClick={() => navigate('/shipping/shipments')}
                  disabled={isValidating}
                >
                  {status
                    ? formatMessage(commonMessages.close)
                    : formatMessage(commonMessages.cancel)}
                </GenericButton>
                {status &&
                  [OrderStatuses.Draft, OrderStatuses.Created, OrderStatuses.Rejected].includes(
                    status as OrderStatuses
                  ) && (
                    <GenericButton
                      variant='contained'
                      color='error'
                      sx={{ ml: '24px', mr: '24px', whiteSpace: 'nowrap' }}
                      onClick={handleAbandon}
                      disabled={isValidating}
                    >
                      {formatMessage(commonMessages.abandon)}
                    </GenericButton>
                  )}
                {currentPage > 1 && (
                  <GenericButton
                    variant='contained'
                    sx={{ marginRight: 1, whiteSpace: 'nowrap' }}
                    onClick={handleBack}
                    disabled={isValidating}
                  >
                    {formatMessage(commonMessages.previous)}
                  </GenericButton>
                )}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {currentPage === 1 && !status && (
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                    <BoxAlert
                      severity='warning'
                      message={
                        isTenantUser
                          ? formatMessage(messages.consignmentTypeCannotBeChangedTenant)
                          : formatMessage(messages.consignmentTypeCannotBeChangedCustomer)
                      }
                      boxProps={{
                        sx: {
                          pb: '0px',
                          m: 0,
                          display: 'flex',
                          alignItems: 'center',
                          minHeight: '40px',
                        },
                      }}
                    />
                  </Box>
                )}
                {currentPage === 3 && (!status || status === OrderStatuses.Draft) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                    <BoxAlert
                      severity='warning'
                      message={formatMessage(messages.trackingNumbersGenerated)}
                      boxProps={{
                        sx: {
                          pb: '0px',
                          m: 0,
                          display: 'flex',
                          alignItems: 'center',
                          minHeight: '40px',
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 0, flexWrap: 'wrap', alignItems: 'center' }}>
                {[1, 2, 3, 4].includes(currentPage) && (
                  <GenericButton
                    variant='contained'
                    color='primary'
                    onClick={handleSaveDraft}
                    disabled={!isTrackingNumberSetup() || isValidating}
                    sx={{ marginRight: 1, whiteSpace: 'nowrap' }}
                  >
                    {formatMessage(messages.saveAndClose)}
                  </GenericButton>
                )}

                {currentPage === 5 && !isRejected && (
                  <TooltipButton
                    variant='contained'
                    color='success'
                    sx={{ whiteSpace: 'nowrap' }}
                    disabled={
                      isConsignmentRequired ||
                      isLoadRequired ||
                      !isTrackingNumberSetup() ||
                      isValidating ||
                      validationErrors.length > 0
                    }
                    showTooltip={
                      isConsignmentRequired || isLoadRequired || validationErrors.length > 0
                    }
                    tooltipProps={{
                      placement: 'top',
                      title:
                        validationErrors.length > 0
                          ? formatMessage(messages.fixValidationErrors)
                          : getTooltipMessage(),
                      children: <></>,
                    }}
                    onClick={handleSubmitShipment}
                  >
                    {formatMessage(messages.submitShipment)}
                  </TooltipButton>
                )}
                {currentPage < 5 && status !== OrderStatuses.Rejected && (
                  <GenericButton
                    variant='contained'
                    disabled={!isTrackingNumberSetup() || isValidating}
                    onClick={handleNext}
                    color={nextButtonColor()}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {nextButtonText()}
                  </GenericButton>
                )}
              </Box>
            </Box>
            {isTrackingNumberSetup() && isConsignmentRequired && (
              <CustomAlert
                severity='error'
                title='Error'
                message={formatMessage(messages.noConsignment)}
              />
            )}
            {isTrackingNumberSetup() && isLoadRequired && (
              <CustomAlert
                severity='error'
                title='Error'
                message={formatMessage(messages.noLoad)}
              />
            )}
            {isTrackingNumberSetup() && isLoadContentRequired && !isLoadRequired && (
              <CustomAlert
                severity='error'
                title='Error'
                message={formatMessage(messages.noLoadContent)}
              />
            )}
            {isTrackingNumberSetup() && (
              <>
                {currentPage !== 5 ? (
                  <>
                    <ShipmentInformation />
                    {currentPage > 0 && customer && <ConsignmentList />}
                  </>
                ) : (
                  currentPage === 5 && <ShipmentSummary data={watch()} />
                )}
              </>
            )}
          </form>
        </FormProvider>
      </OrderFormProvider>
    </Paper>
  )
}

export default ShipmentForm
